/**
 * Loads a SwiftBrowser app compiled to Wasm (a WASI command) and bridges its
 * ops buffer / event exports to the renderer. See docs/ops-protocol.md,
 * "Events (renderer → Swift)", "Phase 2 additions" and "Executor" (Phase 4).
 *
 * Phase 4 modules export `sb_run_jobs(now_ms) -> delay_ms`, which runs the
 * cooperative executor (Task, .task, Task.sleep). The host drives it: once
 * after `_start`, after every event, and whenever the returned delay elapses
 * (a single `setTimeout`, clamped to at least 4 ms); the pending ops are
 * read and applied after every call. See `JobScheduler`.
 */

import { ConsoleStdout, Directory, type Fd, File, OpenFile, PreopenDirectory, WASI, WASIProcExit, type Inode } from '@bjorn3/browser_wasi_shim';
import { HOME, HOME_ENVIRONMENT, TMP, homeDirectory } from './appFiles';
import { zoneInfoEntries } from './zoneinfo';
import { base64Encode } from './base64';
import { performHostRequest, type HostRequestEnvironment } from './hostRequests';
import { wasmNativeHost } from './nativeHost';
import { isOp, type ElementId, type Op, type RendererEvent, type RequestOp, type SwiftDataOp } from './protocol';

export type OpsSink = (ops: Op[]) => void;

export interface AppHandle {
  /** Forward a tap on element `id` to Swift (`sb_event`) and apply the resulting ops. */
  sendEvent(id: ElementId): void;
  /**
   * Deliver a JSON event through `sb_alloc` + `sb_event_json` and apply the
   * resulting ops. Returns `false` (and does nothing) when the module only
   * speaks the Phase 1 protocol. A `tap` event is routed to `sendEvent`.
   */
  sendEventJson(event: RendererEvent): boolean;
  /** Whether the module exports `sb_alloc` and `sb_event_json`. */
  readonly supportsJsonEvents: boolean;
  /** Whether a `batch` event may be sent through `sendEventJson` (the Wasm runtime; not the mocks). */
  readonly supportsBatchEvents?: boolean;
  /**
   * The current `@State` snapshot (`sb_state_ptr`/`sb_state_len`) as a JSON
   * string, or `null` when the module has no snapshot support or no state.
   */
  snapshotState(): string | null;
  /**
   * Phase 4: run the module's executor now (`sb_run_jobs(performance.now())`),
   * apply the resulting ops and (re)schedule the next run from the returned
   * delay. Returns that delay in ms (`-1`: nothing pending), or `null` when
   * the module has no executor export.
   */
  runJobs(): number | null;
  /** Whether the module exports `sb_run_jobs`. */
  readonly supportsJobs: boolean;
  /** Exit code reported by `_start` (0 when `main` returned normally). */
  readonly exitCode: number;
  readonly instance: WebAssembly.Instance;
}

export interface LoadAppOptions {
  /** `argv` passed to the WASI command; defaults to `["Counter"]`. */
  args?: string[];
  /** `KEY=VALUE` environment entries; defaults to none. */
  env?: string[];
  /** Log every WASI syscall to the console (the shim's own tracing). */
  debug?: boolean;
  /**
   * Phase 11: the app's resources, mounted read-only at `/bundle` (path inside
   * the bundle → bytes); `Bundle.main` reads them. Nothing is mounted without it.
   */
  bundle?: ReadonlyMap<string, Uint8Array>;
  /** Phase 11: receives the app's `UserDefaults` whenever they change (a `defaults` op); the op never reaches `onOps`. */
  onDefaults?: (values: Record<string, unknown>) => void;
  /**
   * Phase 18: receives each saved batch of the app's SwiftData stores (a
   * `swiftdata` op), in order; the op never reaches `onOps`.
   */
  onSwiftData?: (op: SwiftDataOp) => void;
  /**
   * Phase 19: the app's home, mounted writable at `/data` (`HOME`, with the
   * XDG variables pointing at its `Library`); an empty one when not given. A
   * fresh in-memory `/tmp` is mounted too.
   */
  home?: Directory;
  /** Phase 19: called after an event or executor run in which the app wrote to its files. */
  onFilesWritten?: () => void;
  /**
   * Phase 13: how `request` ops (`fetch`, `image`) are performed; the
   * browser's `fetch` and `Image` by default. Tests substitute both.
   */
  requests?: HostRequestEnvironment;
}

/** An in-memory WASI directory tree from `path → bytes` (paths `/`-separated, no leading slash). */
export function bundleDirectory(files: ReadonlyMap<string, Uint8Array>): Directory {
  const root = new Directory(new Map<string, Inode>());
  for (const [filePath, bytes] of files) {
    const parts = filePath.split('/').filter((part) => part !== '' && part !== '.' && part !== '..');
    if (parts.length === 0) continue;
    let dir = root;
    for (const part of parts.slice(0, -1)) {
      const existing = dir.contents.get(part);
      let next: Directory;
      if (existing instanceof Directory) {
        next = existing;
      } else {
        next = new Directory(new Map<string, Inode>());
        dir.contents.set(part, next);
      }
      dir = next;
    }
    dir.contents.set(parts[parts.length - 1]!, new File(bytes, { readonly: true }));
  }
  return root;
}

interface SwiftBrowserExports {
  memory: WebAssembly.Memory;
  _start?: () => unknown;
  _initialize?: () => unknown;
  sb_event: (id: number) => void;
  sb_ops_ptr: () => number;
  sb_ops_len: () => number;
  sb_ops_clear: () => void;
  // Phase 2 (optional so Phase 1 modules still load).
  sb_alloc?: (len: number) => number;
  sb_event_json?: (ptr: number, len: number) => void;
  sb_state_ptr?: () => number;
  sb_state_len?: () => number;
  // Phase 4 (optional).
  sb_run_jobs?: (nowMs: number) => number;
  // Bytes beside the JSON (optional; docs/ops-protocol.md, "Bytes").
  sb_bytes?: (ptr: number, len: number) => number;
  sb_bytes_count?: () => number;
  sb_bytes_ptr?: (index: number) => number;
  sb_bytes_len?: (index: number) => number;
}

const WASI_MODULE = 'wasi_snapshot_preview1';
const REQUIRED_EXPORTS = ['memory', 'sb_event', 'sb_ops_ptr', 'sb_ops_len', 'sb_ops_clear'] as const;

export class WasmLoadError extends Error {
  override name = 'WasmLoadError';
}

/** `setTimeout` is clamped to this so a busy executor never spins the main thread. */
export const MIN_JOB_DELAY_MS = 4;

/** The timer surface `JobScheduler` uses (the window's by default; tests stub it). */
export interface JobTimers {
  now(): number;
  setTimeout(callback: () => void, ms: number): number;
  clearTimeout(handle: number): void;
}

const defaultTimers: JobTimers = {
  now: () => performance.now(),
  setTimeout: (callback, ms) => window.setTimeout(callback, ms),
  clearTimeout: (handle) => window.clearTimeout(handle),
};

/**
 * Drives a cooperative executor: `run(now)` runs the due jobs and returns the
 * milliseconds until the next timer (`< 0`: none). `kick()` runs it right away
 * and schedules the next run; there is only ever one pending timer, which
 * every `kick` cancels and replaces, so a burst of events never piles up
 * extra wake-ups. `apply()` is called after every run to drain the ops buffer.
 */
export class JobScheduler {
  private timer: number | null = null;
  /** How many times `run` was called (for tests and debugging). */
  runs = 0;
  /** The delay the last run returned (`-1` when nothing is pending). */
  lastDelay = -1;
  private disposed = false;

  constructor(
    private readonly run: (nowMs: number) => number,
    private readonly apply: () => void,
    private readonly timers: JobTimers = defaultTimers,
    private readonly onError: (error: unknown) => void = (error) => console.error('[sb] sb_run_jobs failed', error),
  ) {}

  /** Run the executor now, apply its ops and reschedule. Returns the delay. */
  kick(): number {
    this.cancel();
    if (this.disposed) return -1;
    let delay = -1;
    try {
      const raw = this.run(this.timers.now());
      delay = Number.isFinite(raw) ? raw : -1;
      this.runs += 1;
      this.apply();
    } catch (error) {
      this.onError(error);
      delay = -1;
    }
    this.lastDelay = delay;
    if (delay >= 0 && !this.disposed) {
      this.timer = this.timers.setTimeout(() => {
        this.timer = null;
        this.kick();
      }, Math.max(MIN_JOB_DELAY_MS, delay));
    }
    return delay;
  }

  /** Whether a timer is waiting to run the executor again. */
  get pending(): boolean {
    return this.timer !== null;
  }

  /** Cancel the pending timer (the next `kick` schedules again). */
  cancel(): void {
    if (this.timer !== null) {
      this.timers.clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /** Cancel and refuse further runs. */
  dispose(): void {
    this.disposed = true;
    this.cancel();
  }
}

export async function loadApp(
  url: string,
  onOps: OpsSink,
  options: LoadAppOptions = {},
): Promise<AppHandle> {
  const module = await compile(url);
  checkImports(module);

  const fds: Fd[] = [
    new OpenFile(new File([])), // stdin: empty
    ConsoleStdout.lineBuffered((line) => console.log(`[wasm stdout] ${line}`)),
    ConsoleStdout.lineBuffered((line) => console.error(`[wasm stderr] ${line}`)),
  ];
  if (options.bundle) {
    // Preopened as fd 3; WASI libc resolves `/bundle/...` paths through it.
    const root = bundleDirectory(options.bundle);
    fds.push(new PreopenDirectory('/bundle', root.contents));
  }
  // Phase 19: the home directory and the temporary directory.
  fds.push(new PreopenDirectory(HOME, (options.home ?? homeDirectory(new Map())).contents));
  fds.push(new PreopenDirectory(TMP, new Map<string, Inode>()));
  // The zone files CoreFoundation reads for the viewer's `TZ` (zoneinfo.ts).
  fds.push(new PreopenDirectory('/usr/share/zoneinfo', zoneInfoEntries()));
  const given = new Set((options.env ?? []).map((entry) => entry.split('=', 1)[0]));
  const env = [...HOME_ENVIRONMENT.filter((entry) => !given.has(entry.split('=', 1)[0])), ...(options.env ?? [])];
  const wasi = new WASI(options.args ?? ['Counter'], env, fds, { debug: options.debug ?? false });
  // Any call that can change a file marks the files as written; the loader
  // reports that once per drain.
  let filesWritten = false;
  const imports = wasi.wasiImport as Record<string, (...args: never[]) => unknown>;
  for (const name of ['fd_pwrite', 'fd_allocate', 'fd_filestat_set_size', 'path_create_directory', 'path_remove_directory',
    'path_unlink_file', 'path_rename', 'path_link']) {
    const original = imports[name];
    if (!original) continue;
    imports[name] = (...args: never[]) => {
      filesWritten = true;
      return original(...args);
    };
  }
  const writeImport = imports['fd_write'];
  if (writeImport) {
    imports['fd_write'] = (...args: never[]) => {
      if ((args[0] as unknown as number) > 2) filesWritten = true;
      return writeImport(...args);
    };
  }
  const openImport = imports['path_open'];
  if (openImport) {
    imports['path_open'] = (...args: never[]) => {
      // oflags: CREAT (1) or TRUNC (8) change the tree or a file.
      if (((args[4] as unknown as number) & 9) !== 0) filesWritten = true;
      return openImport(...args);
    };
  }

  // A native app's engine: the bundle it evaluates runs at the page's global
  // scope, as `run-wasi.mjs` runs it at Node's. A pass the app applies from
  // the page's own loop (a fetch's answer, a timer) mutates the tree the
  // native views observe; the executor runs after it so the shim draws the
  // change, as it runs after an event. Inside an event or a run it waits
  // for that to end (the executor is not re-entered).
  let hostDepth = 0;
  let kickWanted = false;
  let kick: (() => void) | null = null;
  const guarded = <T>(work: () => T): T => {
    hostDepth++;
    try {
      return work();
    } finally {
      hostDepth--;
      if (hostDepth === 0 && kickWanted) {
        kickWanted = false;
        kick?.();
      }
    }
  };
  const nativeHost = importsNativeHost(module)
    ? wasmNativeHost({
        afterInvoke: (name) => {
          if (name !== 'applyOps' || !kick) return;
          if (hostDepth > 0) kickWanted = true;
          else kick();
        },
      })
    : null;
  const instance = await WebAssembly.instantiate(module, {
    [WASI_MODULE]: wasi.wasiImport,
    ...(nativeHost ? nativeHost.imports : {}),
  });
  nativeHost?.attach(instance);
  const exports = instance.exports as unknown as SwiftBrowserExports;
  for (const name of REQUIRED_EXPORTS) {
    if (!(name in exports)) {
      throw new WasmLoadError(`${url} does not export \`${name}\`; see docs/ops-protocol.md`);
    }
  }

  // Run `main`. For a WASI command this is `_start`; it returns (or raises
  // `proc_exit`) after `main` has mounted the root view and performed the
  // first render pass into the pending buffer. The instance and its linear
  // memory stay alive afterwards, so later `sb_event` calls are fine.
  // A reactor-style build (`-mexec-model=reactor`) exposes `_initialize`
  // instead; support it too.
  let exitCode = 0;
  if (typeof exports._start === 'function') {
    try {
      exitCode = wasi.start(instance as { exports: { memory: WebAssembly.Memory; _start: () => unknown } });
    } catch (error) {
      if (error instanceof WASIProcExit) {
        exitCode = error.code;
      } else {
        throw error;
      }
    }
  } else if (typeof exports._initialize === 'function') {
    wasi.initialize(instance as { exports: { memory: WebAssembly.Memory; _initialize?: () => unknown } });
  } else {
    throw new WasmLoadError(`${url} exports neither \`_start\` nor \`_initialize\``);
  }
  if (exitCode !== 0) {
    throw new WasmLoadError(`${url} exited with code ${exitCode}`);
  }

  // Phase 13: `request` ops are answered asynchronously with a `response`
  // event through the same path as the renderer's events, which also runs
  // the executor so the awaiting Swift task resumes.
  const requests = options.requests;
  let deliver: (event: RendererEvent) => boolean = () => false;
  const perform = (op: RequestOp): void => {
    performHostRequest(op, requests).then(
      (value) => deliver({ type: 'response', id: op.id, value }),
      (error: unknown) => deliver({ type: 'response', id: op.id, error: error instanceof Error ? error.message : String(error) }),
    );
  };

  const drain = (): void => {
    let ops = readPendingOps(exports);
    // Clear before applying so a renderer error never re-applies stale ops.
    exports.sb_ops_clear();
    if (ops.some((op) => op.op === 'defaults')) {
      // The last `defaults` op of a batch holds the whole dictionary.
      const latest = ops.filter((op) => op.op === 'defaults').pop();
      if (latest && latest.op === 'defaults') options.onDefaults?.(latest.values);
      ops = ops.filter((op) => op.op !== 'defaults');
    }
    if (ops.some((op) => op.op === 'swiftdata')) {
      // Every save matters (each holds only what changed), in order.
      for (const op of ops) if (op.op === 'swiftdata') options.onSwiftData?.(op);
      ops = ops.filter((op) => op.op !== 'swiftdata');
    }
    if (ops.some((op) => op.op === 'request')) {
      for (const op of ops) if (op.op === 'request') perform(op);
      ops = ops.filter((op) => op.op !== 'request');
    }
    if (ops.length > 0) onOps(ops);
    if (filesWritten) {
      filesWritten = false;
      options.onFilesWritten?.();
    }
  };

  const supportsJsonEvents =
    typeof exports.sb_alloc === 'function' && typeof exports.sb_event_json === 'function';
  const supportsSnapshot =
    typeof exports.sb_state_ptr === 'function' && typeof exports.sb_state_len === 'function';
  const supportsJobs = typeof exports.sb_run_jobs === 'function';

  // Phase 4: the executor runs after the first ops and after every event; the
  // scheduler's own timer covers sleeps and clocks in between.
  const scheduler = supportsJobs ? new JobScheduler((now) => exports.sb_run_jobs!(now), drain) : null;
  const runJobs = (): number | null => guarded(() => (scheduler ? scheduler.kick() : null));
  if (nativeHost) kick = () => runJobs();

  const sendEvent = (id: ElementId): void =>
    guarded(() => {
      exports.sb_event(id | 0);
      drain();
      runJobs();
    });

  /** `bytes` in linear memory for the next event (`sb_bytes`), as the event names them. */
  const putBytes = (bytes: Uint8Array): { $bytes: number } | string => {
    if (typeof exports.sb_bytes !== 'function') return base64Encode(bytes);
    const ptr = writeBytes(exports, bytes);
    return { $bytes: exports.sb_bytes(ptr, bytes.byteLength) };
  };

  const sendEventJson = (event: RendererEvent): boolean => {
    if (event.type === 'tap' && event.x === undefined) {
      // A tap with a point (`onTapGesture { point in }`) goes as JSON.
      sendEvent(event.id);
      return true;
    }
    if (!supportsJsonEvents) return false;
    // A fetched body crosses as bytes: copied into linear memory and named
    // `{"$bytes": n}` in the event, or as base64 for a module without `sb_bytes`.
    const json = event.type === 'response' ? JSON.stringify(event, (_key, value: unknown) => (value instanceof Uint8Array ? putBytes(value) : value)) : JSON.stringify(event);
    const bytes = encoder.encode(json);
    guarded(() => {
      const ptr = writeBytes(exports, bytes);
      exports.sb_event_json!(ptr, bytes.byteLength);
      drain();
      runJobs();
    });
    return true;
  };
  deliver = sendEventJson;

  drain();
  runJobs();

  return {
    instance,
    exitCode,
    supportsJsonEvents,
    supportsBatchEvents: supportsJsonEvents,
    supportsJobs,
    runJobs,
    sendEvent,
    sendEventJson,
    snapshotState(): string | null {
      if (!supportsSnapshot) return null;
      // `sb_state_ptr` refreshes the buffer, so call it before `sb_state_len`.
      const ptr = exports.sb_state_ptr!() >>> 0;
      const len = exports.sb_state_len!() >>> 0;
      if (len === 0) return null;
      const memory = exports.memory.buffer;
      if (ptr + len > memory.byteLength) {
        throw new WasmLoadError(
          `sb_state_ptr/len (${ptr}, ${len}) point outside linear memory (${memory.byteLength} bytes)`,
        );
      }
      return decoder.decode(new Uint8Array(memory, ptr, len));
    },
  };
}

/** The module at `url`, whole or joined from the parts it was published as. */
export async function compile(url: string, fetchImpl: typeof fetch = fetch): Promise<WebAssembly.Module> {
  const response = await fetchImpl(url);
  const type = response.headers.get('content-type') ?? '';
  if (response.ok && typeof WebAssembly.compileStreaming === 'function' && type.includes('application/wasm')) {
    try {
      return await WebAssembly.compileStreaming(response);
    } catch (error) {
      // A host may answer a missing file with its fallback page and still
      // label it a Wasm module (Cloudflare Pages does, with the `/*.wasm`
      // Content-Type rule in public/_headers): the module may be published
      // as parts.
      const joined = await fetchParts(url, fetchImpl);
      if (joined) return WebAssembly.compile(joined);
      throw error;
    }
  }
  // Fall back for servers that mislabel the MIME type.
  const bytes = response.ok ? await response.arrayBuffer() : new ArrayBuffer(0);
  if (bytes.byteLength < 8 || new DataView(bytes).getUint32(0, true) !== 0x6d736100) {
    // A module larger than the host allows a file to be is published as
    // parts (plugins/swift-wasm.ts); the host answers the whole one's URL
    // with a 404 or its fallback page.
    const joined = await fetchParts(url, fetchImpl);
    if (joined) return WebAssembly.compile(joined);
    if (!response.ok) throw new WasmLoadError(`Failed to fetch ${url}: ${response.status} ${response.statusText}`);
    throw new WasmLoadError(
      `${url} is not a Wasm binary (${bytes.byteLength} bytes, content-type "${type}"). ` +
        'Has scripts/build-wasm.sh copied the module into Web/public/?',
    );
  }
  return WebAssembly.compile(bytes);
}

/**
 * The module published as parts at `<url>.parts.json` (`{ size, parts }`),
 * fetched in parallel and joined; null when there is no such manifest.
 */
export async function fetchParts(url: string, fetchImpl: typeof fetch = fetch): Promise<Uint8Array<ArrayBuffer> | null> {
  const manifestURL = `${url}.parts.json`;
  let manifest: { size?: unknown; parts?: unknown };
  try {
    const response = await fetchImpl(manifestURL);
    if (!response.ok) return null;
    manifest = (await response.json()) as typeof manifest;
  } catch {
    return null;
  }
  const { size, parts } = manifest;
  if (typeof size !== 'number' || !Array.isArray(parts) || !parts.every((part) => typeof part === 'string')) {
    throw new WasmLoadError(`${manifestURL} is not a parts manifest`);
  }
  const base = new URL(manifestURL, typeof location === 'undefined' ? 'http://localhost/' : location.href);
  const chunks = await Promise.all(parts.map(async (part) => {
    const partURL = new URL(part, base).href;
    const response = await fetchImpl(partURL);
    if (!response.ok) throw new WasmLoadError(`Failed to fetch ${partURL}: ${response.status} ${response.statusText}`);
    return new Uint8Array(await response.arrayBuffer());
  }));
  const joined = new Uint8Array(size);
  let offset = 0;
  for (const chunk of chunks) {
    if (offset + chunk.byteLength > size) throw new WasmLoadError(`${manifestURL}: the parts are larger than ${size} bytes`);
    joined.set(chunk, offset);
    offset += chunk.byteLength;
  }
  if (offset !== size) throw new WasmLoadError(`${manifestURL}: the parts hold ${offset} of ${size} bytes`);
  return joined;
}

/**
 * A SwiftBrowser Native app built for the browser (docs/native.md, "The round
 * trip": the native views over the shim, with the host's JavaScript as the
 * engine) imports `sb_native.request`; the page answers it (nativeHost.ts).
 */
const NATIVE_MODULE = 'sb_native';

function checkImports(module: WebAssembly.Module): void {
  const foreign = WebAssembly.Module.imports(module).filter((i) => i.module !== WASI_MODULE && i.module !== NATIVE_MODULE);
  if (foreign.length > 0) {
    const list = foreign.map((i) => `${i.module}.${i.name}`).join(', ');
    throw new WasmLoadError(
      `Module imports symbols outside ${WASI_MODULE}: ${list}. ` +
        `The renderer only provides WASI preview1 (and ${NATIVE_MODULE} for a native app).`,
    );
  }
}

/** Whether the module runs a native app with the page's JavaScript as its engine. */
export function importsNativeHost(module: WebAssembly.Module): boolean {
  return WebAssembly.Module.imports(module).some((i) => i.module === NATIVE_MODULE);
}

const decoder = new TextDecoder('utf-8', { fatal: true });
const encoder = new TextEncoder();

/** Copies `bytes` into a buffer from `sb_alloc` and answers where it starts. */
function writeBytes(exports: SwiftBrowserExports, bytes: Uint8Array): number {
  const ptr = exports.sb_alloc!(bytes.byteLength) >>> 0;
  // `memory.buffer` must be read after `sb_alloc`: allocating may grow
  // linear memory and detach the previous ArrayBuffer.
  const memory = exports.memory.buffer;
  if (ptr + bytes.byteLength > memory.byteLength) {
    throw new WasmLoadError(`sb_alloc(${bytes.byteLength}) returned ${ptr}, outside linear memory (${memory.byteLength} bytes)`);
  }
  new Uint8Array(memory, ptr, bytes.byteLength).set(bytes);
  return ptr;
}

/** The pending ops' `count` byte buffers (`sb_bytes_*`), each copied out of linear memory. */
function readPendingBytes(exports: SwiftBrowserExports, count: number): Uint8Array[] {
  if (count === 0 || typeof exports.sb_bytes_ptr !== 'function' || typeof exports.sb_bytes_len !== 'function') return [];
  const out: Uint8Array[] = [];
  for (let i = 0; i < count; i++) {
    const ptr = exports.sb_bytes_ptr(i) >>> 0;
    const len = exports.sb_bytes_len(i) >>> 0;
    const memory = exports.memory.buffer;
    if (ptr + len > memory.byteLength) {
      throw new WasmLoadError(`sb_bytes_ptr/len(${i}) (${ptr}, ${len}) point outside linear memory (${memory.byteLength} bytes)`);
    }
    out.push(new Uint8Array(memory, ptr, len).slice());
  }
  return out;
}

/** `value` with each `{"$bytes": n}` replaced by the `n`th buffer. */
function resolveBytes(value: unknown, buffers: readonly Uint8Array[]): unknown {
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) value[i] = resolveBytes(value[i], buffers);
    return value;
  }
  if (typeof value !== 'object' || value === null) return value;
  const record = value as Record<string, unknown>;
  const n = record['$bytes'];
  if (typeof n === 'number' && Object.keys(record).length === 1) {
    const buffer = buffers[n];
    if (!buffer) throw new WasmLoadError(`Pending ops name bytes ${n}, but there are ${buffers.length}`);
    return buffer;
  }
  for (const key in record) record[key] = resolveBytes(record[key], buffers);
  return record;
}

/**
 * Read the pending ops buffer. `memory.buffer` is re-read on every call: the
 * ArrayBuffer is detached whenever linear memory grows.
 */
function readPendingOps(exports: SwiftBrowserExports): Op[] {
  // Asked first: it tells Swift this page reads bytes beside the JSON, so
  // the ops it serializes next name them (docs/ops-protocol.md, "Bytes").
  const byteCount = typeof exports.sb_bytes_count === 'function' && typeof exports.sb_bytes_ptr === 'function' && typeof exports.sb_bytes_len === 'function' ? exports.sb_bytes_count() >>> 0 : 0;
  const ptr = exports.sb_ops_ptr() >>> 0;
  const len = exports.sb_ops_len() >>> 0;
  if (len === 0) return [];
  const memory = exports.memory.buffer;
  if (ptr + len > memory.byteLength) {
    throw new WasmLoadError(
      `sb_ops_ptr/len (${ptr}, ${len}) point outside linear memory (${memory.byteLength} bytes)`,
    );
  }
  const bytes = new Uint8Array(memory, ptr, len);
  let json: string;
  try {
    json = decoder.decode(bytes);
  } catch (error) {
    throw new WasmLoadError(`Pending ops buffer is not valid UTF-8: ${String(error)}`);
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch (error) {
    throw new WasmLoadError(`Pending ops buffer is not valid JSON: ${String(error)}\n${json}`);
  }
  if (!Array.isArray(parsed)) {
    throw new WasmLoadError(`Pending ops buffer must be a JSON array, got ${typeof parsed}`);
  }
  const bad = parsed.find((item) => !isOp(item));
  if (bad !== undefined) {
    throw new WasmLoadError(`Unknown op in pending buffer: ${JSON.stringify(bad)}`);
  }
  // Image files and other bytes come beside the JSON (docs/ops-protocol.md, "Bytes").
  const buffers = readPendingBytes(exports, byteCount);
  if (buffers.length > 0) resolveBytes(parsed, buffers);
  return parsed as Op[];
}
